import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CreditCard, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { formatINR, pad, hourLabel } from '@/lib/utils';
import { useToast } from '@/components/ui/Toast';
import { usePayment } from '@/lib/payment';
import { useAuth } from '@/lib/auth';
import { useVenue } from '@/lib/venue';


function getLocalIsoDate(date = new Date()) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function parseLocalDate(isoDate) {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export default function BookCourt() {
  const [step, setStep] = useState(0);
  const [courts, setCourts] = useState([]);
  const [sports, setSports] = useState([]);
  const [sportId, setSportId] = useState(null);
  const [court, setCourt] = useState(null);
  const [date, setDate] = useState(() => getLocalIsoDate());
  const [slots, setSlots] = useState([]);
  const [startHour, setStartHour] = useState(null);
  const [duration, setDuration] = useState(1);
  const [busy, setBusy] = useState(false);
  const [offers, setOffers] = useState([]);
  const { toast } = useToast();
  const pay = usePayment();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Courts are venue-scoped — see lib/venue.jsx. The chosen venue persists
  // across navigation for the rest of the session (shown in the header via
  // CustomerLayout's "change venue" link) instead of being forced to ask
  // again on every visit — only a first-ever pick (no selection yet, more
  // than one venue) sends the customer to the picker.
  const {
    selectedVenueId,
    needsPicker,
    loading: venuesLoading,
  } = useVenue();

  useEffect(() => {

  if (
    user &&
    !user.phone
  ) {

    navigate('/app/complete-profile', {
      state: {
        redirectTo: '/app/book',
      },
    });
  }

}, [user, navigate]);

  useEffect(() => {
    if (needsPicker) {
      navigate('/app/venues', { state: { redirectTo: '/app/book' } });
    }
  }, [needsPicker, navigate]);

  useEffect(() => {
    api.get('/sports').then((r) => setSports(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (venuesLoading || needsPicker) return;
    const params = selectedVenueId ? { academy_id: selectedVenueId } : {};
    api.get('/courts', { params }).then((r) => setCourts(r.data));
    setCourt(null);
    setSportId(null);
    setStep(0);
  }, [selectedVenueId, venuesLoading, needsPicker]);

  // Sport-first browsing: derive which sports this venue actually offers
  // from its courts (a court belongs to exactly one sport). If the venue
  // only has one sport, skip the picker entirely and auto-select it.
  const venueSports = useMemo(() => {
    const ids = [...new Set(courts.map((c) => c.sport_id).filter(Boolean))];
    return ids
      .map((id) => sports.find((s) => s.id === id))
      .filter(Boolean);
  }, [courts, sports]);
  const needsSportStep = venueSports.length > 1;

  useEffect(() => {
    if (!needsSportStep && venueSports.length === 1 && !sportId) {
      setSportId(venueSports[0].id);
    }
  }, [needsSportStep, venueSports, sportId]);

  const STEPS = needsSportStep ? ['Sport', 'Court', 'Date', 'Time', 'Review'] : ['Court', 'Date', 'Time', 'Review'];
  const filteredCourts = sportId ? courts.filter((c) => c.sport_id === sportId) : courts;

  useEffect(() => {
    if (!court || !date) return;
    api.get(`/courts/${court.id}/availability`, { params: { date } }).then((r) => setSlots(r.data.slots));
    setStartHour(null);
  }, [court, date]);

  // Discounts & offers: purely informational here — the actual charge
  // always comes back from the booking the backend creates (see
  // app.services.best_discount), this just previews what might apply so
  // the customer isn't surprised at payment.
  useEffect(() => {
    if (!court) { setOffers([]); return; }
    api.get('/offers', { params: { academy_id: court.academy_id } })
      .then((r) => setOffers((r.data || []).filter((o) => o.applies_to !== 'membership')))
      .catch(() => setOffers([]));
  }, [court]);

  // Peak-priced hours cost more than hourly_rate — sum each covered slot's
  // own rate (from availability) rather than hourly_rate * duration, so
  // this matches what the backend actually charges (see price_for_slots).
  const total = startHour == null
    ? 0
    : Array.from({ length: duration }).reduce(
        (sum, _, i) => sum + (slots.find((s) => s.hour === startHour + i)?.rate ?? court?.hourly_rate ?? 0),
        0,
      );

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));
  const pickSport = (id) => { setSportId(id); setCourt(null); goNext(); };
  const currentStep = STEPS[step];

  const confirmAndPay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/bookings', {
        court_id: court.id,
        booking_date: date,
        start_hour: startHour,
        duration_hours: duration,
      });
      pay({
        purpose: 'booking',
        reference_id: data.id,
        onSuccess: () => {
          toast({ title: 'Booking confirmed!', description: 'Email receipt sent', variant: 'success' });
          setTimeout(() => navigate('/app/bookings'), 800);
        },
      });
    } catch (e) {
      toast({ title: 'Booking failed', description: e.response?.data?.detail || 'Try a different slot', variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="book-court-page" className="space-y-8">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Court Reservation</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tight">Book a Court</h1>
      </div>

      <Progress step={step} steps={STEPS} />

      {currentStep === 'Sport' && (
        <Step title="Pick a sport">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {venueSports.map((s) => (
              <button
                type="button"
                key={s.id}
                onClick={() => pickSport(s.id)}
                data-testid={`sport-pick-${s.id}`}
                className="text-left bg-surface border border-white/10 hover:border-volt p-6 flex flex-col items-center gap-3 transition-colors"
              >
                <span className="text-3xl">{s.icon || '🏸'}</span>
                <span className="font-heading font-bold uppercase text-sm">{s.name}</span>
              </button>
            ))}
          </div>
          <NavRow>
            <span />
            <span />
          </NavRow>
        </Step>
      )}

      {currentStep === 'Court' && (
        <Step title="Pick a court">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCourts.map((c) => {
              const active = court?.id === c.id;
              return (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => setCourt(c)}
                  data-testid={`court-pick-${c.id}`}
                  className={`text-left bg-surface border ${active ? 'border-volt volt-glow' : 'border-white/10 hover:border-white/30'} transition-colors group`}
                >
                  <div className="aspect-[4/3] bg-obsidian overflow-hidden">
                    {c.image_url && <img src={c.image_url} alt={c.name} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" />}
                  </div>
                  <div className="p-4">
                    <div className="font-heading font-bold uppercase">{c.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1">{hourLabel(c.open_hour)} – {hourLabel(c.close_hour)}</div>
                    <div className="mt-3 text-volt font-mono">{formatINR(c.hourly_rate)}<span className="text-white/40 text-xs"> /hr</span></div>
                    {(c.peak_windows || []).map((w, i) => (
                      <div key={i} className="text-[10px] text-white/40 mt-1">Peak {hourLabel(w.start_hour)}–{hourLabel(w.end_hour)}: {formatINR(w.rate)}/hr</div>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
          <NavRow>
            {needsSportStep ? <Button variant="outline" onClick={goBack}><ArrowLeft size={14} /> Change sport</Button> : <span />}
            <Button onClick={goNext} disabled={!court} data-testid="step-next">Continue <ArrowRight size={14} /></Button>
          </NavRow>
        </Step>
      )}

      {currentStep === 'Date' && (
        <Step title={`Pick a date for ${court?.name}`}>
          <DatePicker date={date} setDate={setDate} />
          <NavRow>
            <Button variant="outline" onClick={goBack}><ArrowLeft size={14} /> Back</Button>
            <Button onClick={goNext} data-testid="step-next">Continue <ArrowRight size={14} /></Button>
          </NavRow>
        </Step>
      )}

      {currentStep === 'Time' && (
        <Step title="Pick a time slot">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-white/60">Duration</span>
            <div className="flex gap-1">
              {[1, 2].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDuration(d)}
                  className={`px-4 h-9 text-xs font-bold uppercase tracking-wider border ${duration === d ? 'border-volt text-volt bg-volt/5' : 'border-white/15 text-white/60 hover:border-white/30'}`}
                  data-testid={`duration-${d}`}
                >{d}h</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 gap-2">
            {slots.map((s) => {
              const canSelect = s.available && (s.hour + duration <= court.close_hour) && Array.from({ length: duration }).every((_, i) => {
                const next = slots.find((x) => x.hour === s.hour + i);
                return next && next.available;
              });
              const active = startHour === s.hour;
              const isYours = s.reason === 'yours';
              const isMember = s.reason === 'member_reserved';
              const isAdminBlocked = s.reason === 'admin_blocked';
              const isPast = s.reason === 'past';
              const label = isYours ? 'Yours' : isMember ? 'Held' : isAdminBlocked ? 'Blocked' : isPast ? 'Past' : null;
              const cls = active
                ? 'border-volt bg-volt text-black'
                : canSelect
                ? 'border-white/15 text-white hover:border-volt'
                : isYours
                ? 'border-volt/50 bg-volt/10 text-volt cursor-not-allowed'
                : isAdminBlocked
                ? 'border-[#FF3B30]/40 bg-[#FF3B30]/5 text-[#FF3B30]/70 cursor-not-allowed'
                : isPast
                ? 'border-white/10 text-white/25 cursor-not-allowed bg-white/[0.02]'
                : 'border-white/5 text-white/20 line-through cursor-not-allowed bg-white/[0.02]';
              return (
                <button
                  key={s.hour}
                  type="button"
                  disabled={!canSelect}
                  onClick={() => setStartHour(s.hour)}
                  data-testid={`slot-${s.hour}`}
                  className={`h-14 flex flex-col items-center justify-center gap-0.5 text-xs font-bold uppercase tracking-wider border ${cls}`}
                  title={isYours ? 'Already part of your daily membership reservation — no booking needed' : isMember ? 'Reserved by another member' : isAdminBlocked ? 'Blocked by the venue' : isPast ? 'This time has already passed' : ''}
                >
                  <span>{pad(s.hour)}:00</span>
                  {label ? (
                    <span className="text-[9px] tracking-[0.2em] opacity-90">{label}</span>
                  ) : (
                    <span className="text-[9px] tracking-[0.2em] opacity-70">₹{s.rate}</span>
                  )}
                </button>
              );
            })}
          </div>
          {slots.some((s) => s.reason === 'yours') && (
            <div className="text-xs text-volt mt-2">✓ Slots labelled <span className="font-bold">YOURS</span> are already part of your daily membership reservation — show up at the court, no booking needed.</div>
          )}
          <NavRow>
            <Button variant="outline" onClick={goBack}><ArrowLeft size={14} /> Back</Button>
            <Button onClick={goNext} disabled={startHour == null} data-testid="step-next">Review <ArrowRight size={14} /></Button>
          </NavRow>
        </Step>
      )}

      {currentStep === 'Review' && (
        <Step title="Review & pay">
          <div className="bg-surface border border-white/10 p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              {needsSportStep && <Row label="Sport" value={venueSports.find((s) => s.id === sportId)?.name || '—'} />}
              <Row label="Court" value={court.name} />
              <Row label="Date" value={parseLocalDate(date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'long', year: 'numeric', weekday: 'long' })} />
              <Row label="Time" value={`${pad(startHour)}:00 – ${pad(startHour + duration)}:00`} />
              <Row label="Duration" value={`${duration} hour${duration > 1 ? 's' : ''}`} />
            </div>
            <div className="bg-obsidian p-6 border border-white/10">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">Total payable</div>
              <div className="font-heading font-black text-5xl text-volt">{formatINR(total)}</div>
              {offers.length > 0 ? (
                <div className="mt-3 space-y-1">
                  {offers.map((o) => (
                    <p key={o.id} className="text-xs text-volt">
                      🎉 {o.title} — {o.discount_type === 'percent' ? `${o.discount_value}%` : formatINR(o.discount_value)} off
                    </p>
                  ))}
                  <p className="text-[10px] text-white/40">The best discount you qualify for (this offer or your membership) is applied automatically at payment.</p>
                </div>
              ) : (
                <p className="text-xs text-white/40 mt-3">Member discount (if any) applied automatically at payment.</p>
              )}
              <Button className="w-full mt-6" onClick={confirmAndPay} disabled={busy} data-testid="confirm-pay-button">
                {busy ? <><Loader2 size={14} className="animate-spin" /> Processing</> : <><CreditCard size={14} /> Confirm & Pay</>}
              </Button>
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/30 mt-4 text-center">
                Powered by Razorpay · Email receipt instant
              </p>
            </div>
          </div>
          <NavRow>
            <Button variant="outline" onClick={goBack}><ArrowLeft size={14} /> Back</Button>
            <span />
          </NavRow>
        </Step>
      )}
    </div>
  );
}

function Progress({ step, steps }) {
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
      {steps.map((s, i) => (
        <div key={s} className="space-y-2">
          <div className={`h-1.5 ${i <= step ? 'bg-volt' : 'bg-white/10'}`} />
          <div className={`text-[10px] uppercase tracking-[0.25em] ${i <= step ? 'text-volt' : 'text-white/30'}`}>
            {String(i + 1).padStart(2, '0')} · {s}
          </div>
        </div>
      ))}
    </div>
  );
}

function Step({ title, children }) {
  return (
    <div className="space-y-5">
      <h2 className="font-heading font-bold text-2xl uppercase tracking-tight text-white/80">{title}</h2>
      {children}
    </div>
  );
}

function NavRow({ children }) {
  return <div className="flex items-center justify-between pt-4">{children}</div>;
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</div>
      <div className="text-base font-medium text-white mt-1">{value}</div>
    </div>
  );
}

function DatePicker({ date, setDate }) {
  const today = parseLocalDate(getLocalIsoDate());
  const days = Array.from({ length: 14 }).map((_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    return d;
  });
  return (
    <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
      {days.map((d, idx) => {
        const iso = getLocalIsoDate(d);
        const active = iso === date;
        const isToday = idx === 0;
        return (
          <button
            key={iso}
            type="button"
            onClick={() => setDate(iso)}
            data-testid={`date-${iso}`}
            className={`relative p-3 border ${active ? 'border-volt bg-volt/10 text-volt' : 'border-white/10 text-white hover:border-volt/60 hover:bg-white/[0.03]'} text-center transition-colors`}
          >
            {isToday && !active && <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-volt rounded-full" />}
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">{d.toLocaleDateString('en-IN', { weekday: 'short' })}</div>
            <div className="font-heading font-bold text-2xl mt-1">{d.getDate()}</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">{d.toLocaleDateString('en-IN', { month: 'short' })}</div>
          </button>
        );
      })}
    </div>
  );
}
